import { Change, PropertyChanges } from "../types";

/** The part of a property's config the comparison needs. */
export type ComparableProperty = {
  getItemKey?: (item: unknown) => string;
  default?: unknown;
};

/**
 * Diffs the two sides of a section, one entry of `properties` at a time —
 * anything the selectors return but `properties` does not declare is ignored.
 */
export function compareSection(
  savedValues: Record<string, unknown> | null,
  currentValues: Record<string, unknown> | null,
  properties: Record<string, ComparableProperty>
): PropertyChanges[] {
  const saved = savedValues ?? {};
  const current = currentValues ?? {};
  // A section that appears or disappears brings its defaults into effect, so
  // they count as changes; while it stands on both sides they are noise.
  const sectionExistsOnBothSides =
    Boolean(savedValues) && Boolean(currentValues);
  const results: PropertyChanges[] = [];

  for (const [name, property] of Object.entries(properties)) {
    if (
      sectionExistsOnBothSides &&
      isUntouchedDefault(saved, current, name, property)
    ) {
      continue;
    }

    const changes = compareValues(
      saved[name],
      current[name],
      property.getItemKey
    );
    if (changes.length > 0) {
      results.push({ property: name, changes });
    }
  }

  return results;
}

/**
 * True when the property is on one side only and holds its default value: the
 * form filled it in and nobody touched it. Checking the pair rather than each
 * side keeps a real toggle visible even when it lands on the default.
 */
function isUntouchedDefault(
  saved: Record<string, unknown>,
  current: Record<string, unknown>,
  name: string,
  property: ComparableProperty
): boolean {
  if (!("default" in property)) return false;

  const inSaved = name in saved;
  const inCurrent = name in current;
  if (inSaved === inCurrent) return false;

  return (inSaved ? saved[name] : current[name]) === property.default;
}

function compareValues(
  saved: unknown,
  current: unknown,
  getItemKey?: (item: unknown) => string
): Change[] {
  // Lists are diffed item by item even when one side is absent, so a list
  // that appears or disappears lists each of its entries.
  if (Array.isArray(saved) || Array.isArray(current)) {
    return compareArrays(
      Array.isArray(saved) ? saved : [],
      Array.isArray(current) ? current : [],
      getItemKey
    );
  }

  if (isAbsent(saved) && isAbsent(current)) return [];

  if (isAbsent(saved)) {
    return [{ type: "added", value: current }];
  }

  if (isAbsent(current)) {
    return [{ type: "deleted", value: saved }];
  }

  if (areDeepEqual(saved, current)) return [];

  return [{ type: "modified", original: saved, update: current }];
}

/** Pairs items by their key, so an edited item reads as a single change. */
function compareArrays(
  saved: unknown[],
  current: unknown[],
  declaredGetItemKey?: (item: unknown) => string
): Change[] {
  const getItemKey = declaredGetItemKey ?? getDefaultItemKey;
  const savedMap = new Map<string, unknown>();
  const currentMap = new Map<string, unknown>();

  for (const item of saved) {
    savedMap.set(getItemKey(item), item);
  }

  for (const item of current) {
    currentMap.set(getItemKey(item), item);
  }

  const changes: Change[] = [];

  for (const [key, item] of savedMap) {
    const currentItem = currentMap.get(key);

    if (currentItem === undefined) {
      changes.push({ type: "deleted", value: item });
    } else if (!areDeepEqual(item, currentItem)) {
      changes.push({ type: "modified", original: item, update: currentItem });
    }
  }

  for (const [key, item] of currentMap) {
    if (!savedMap.has(key)) {
      changes.push({ type: "added", value: item });
    }
  }

  return changes;
}

/** Used to pair list items when the property declares no `getItemKey`. */
function getDefaultItemKey(item: unknown): string {
  if (typeof item === "string") return item;
  if (typeof item === "number") return String(item);
  if (typeof item === "boolean") return String(item);
  return JSON.stringify(item);
}

function isAbsent(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

export function areDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // The rule leaves unset fields out and the form clears them to null; both
  // mean "no value".
  if (isAbsent(a) || isAbsent(b)) return isAbsent(a) && isAbsent(b);
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => areDeepEqual(item, b[i]));
  }

  if (typeof a === "object" && typeof b === "object") {
    const objA = a as Record<string, unknown>;
    const objB = b as Record<string, unknown>;
    // The union of the keys, so a key missing on one side and empty on the
    // other still compares equal.
    const keys = new Set([...Object.keys(objA), ...Object.keys(objB)]);

    return [...keys].every(key => areDeepEqual(objA[key], objB[key]));
  }

  return false;
}
